import { BrandLockup } from "../Logo";
import { CONTACT } from "../../lib/constants";
import { WhatsAppChatButton, SocialLinks } from "../app/WhatsApp";
import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, Cookie, Download, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth, homeForRole } from "../../context/AuthContext";
import { useInstallPrompt } from "../../lib/pwa";

const INSTALL_BAR_DISMISS_KEY = "kt_install_bar_dismissed";

function InstallPromoBar() {
  const { installed, standalone } = useInstallPrompt();
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(() => localStorage.getItem(INSTALL_BAR_DISMISS_KEY) === "1");
  if (hidden || installed || standalone || pathname === "/download") return null;
  const dismiss = (e) => { e.preventDefault(); localStorage.setItem(INSTALL_BAR_DISMISS_KEY, "1"); setHidden(true); };
  return (
    <div className="border-b border-brand-100 bg-brand-50/70" data-testid="install-promo-bar">
      <div className="kt-container flex items-center gap-3 py-2 text-sm">
        <Download className="h-4 w-4 shrink-0 text-brand" />
        <p className="min-w-0 flex-1 truncate text-ink-soft">
          Get the KohimaTaxi app for a faster experience.
        </p>
        <Link to="/download" className="shrink-0 rounded-full bg-brand px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-600" data-testid="install-bar-cta">
          Install the app
        </Link>
        <button type="button" onClick={dismiss} className="shrink-0 rounded-lg p-1.5 text-ink-muted hover:bg-white hover:text-ink" aria-label="Dismiss" data-testid="install-bar-dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

const NAV = [
  { to: "/for-customers", label: "Customers" },
  { to: "/for-drivers", label: "Drivers" },
  { to: "/for-restaurants", label: "Restaurants" },
  { to: "/stories", label: "Stories" },
  { to: "/download", label: "Download" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Brand({ light = false, compact = false }) {
  return (
    <Link to="/" className="flex items-center" data-testid="brand-logo">
      <BrandLockup light={light} compact={compact} />
    </Link>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className={`sticky top-0 z-50 border-b transition-colors duration-300 ${scrolled ? "border-surface-300 kt-glass" : "border-transparent bg-surface"}`}>
      <div className="kt-container flex h-[72px] items-center justify-between">
        <Brand />
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `kt-nav-link ${isActive ? "text-brand" : ""}`} data-testid={`nav-${n.label.toLowerCase()}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <Button asChild className="rounded-full bg-brand px-5 text-white hover:bg-brand-600" data-testid="nav-open-app">
              <Link to={homeForRole(user.role)}>Open app <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" className="rounded-full text-ink hover:bg-surface-200" data-testid="nav-login">
                <Link to="/auth/login">Log in</Link>
              </Button>
              <Button asChild className="rounded-full bg-brand px-5 text-white hover:bg-brand-600" data-testid="nav-signup">
                <Link to="/auth/signup">Get started</Link>
              </Button>
            </>
          )}
        </div>
        <button className="rounded-lg p-2 text-ink lg:hidden" onClick={() => setOpen((o) => !o)} aria-label="Toggle menu" data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-surface-300 bg-surface lg:hidden" data-testid="nav-mobile-menu">
          <div className="kt-container flex flex-col gap-1 py-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => `rounded-lg px-3 py-2.5 text-base font-medium ${isActive ? "bg-brand-50 text-brand" : "text-ink"}`}>
                {n.label}
              </NavLink>
            ))}
            <div className="mt-3 flex gap-3">
              {user ? (
                <Button asChild className="flex-1 rounded-full bg-brand text-white"><Link to={homeForRole(user.role)}>Open app</Link></Button>
              ) : (
                <>
                  <Button asChild variant="outline" className="flex-1 rounded-full"><Link to="/auth/login">Log in</Link></Button>
                  <Button asChild className="flex-1 rounded-full bg-brand text-white"><Link to="/auth/signup">Get started</Link></Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-surface-300 bg-ink text-surface" data-testid="site-footer">
      <div className="kt-container grid gap-10 py-14 md:grid-cols-4">
        <div className="md:col-span-1">
          <Brand light />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-surface/70">
            Seamless rides. Fresh deliveries. Verified drivers and kitchens, one app for every city in India.
          </p>
          <address className="mt-5 space-y-1.5 text-sm not-italic text-surface/70" data-testid="footer-contact">
            <p>{CONTACT.addressLines[0]},<br />{CONTACT.addressLines[1]}</p>
            <p><a className="hover:text-brand-200" href={CONTACT.phoneHref}>{CONTACT.phone}</a></p>
            <p><a className="hover:text-brand-200" href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></p>
            <p><a className="inline-flex items-center gap-1.5 font-semibold text-sunny-300 hover:text-sunny-200" href={CONTACT.googleProfile} target="_blank" rel="noopener noreferrer" data-testid="footer-google-profile"><MapPin className="h-4 w-4" /> Find us on Google</a></p>
          </address>
          <WhatsAppChatButton variant="light" className="mt-4" label="WhatsApp support" testId="footer-whatsapp" />
          <p className="mt-3 text-xs text-surface/70" data-testid="footer-whatsapp-updates">Get ride &amp; order updates on WhatsApp — turn it on under Profile → WhatsApp updates after you sign in.</p>
          <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-surface/60">Follow us</p>
          <SocialLinks light className="mt-2" testId="footer-social" />
        </div>
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-surface/60">Product</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><Link className="hover:text-brand-200" to="/for-customers">For customers</Link></li>
            <li><Link className="hover:text-brand-200" to="/for-drivers">For drivers</Link></li>
            <li><Link className="hover:text-brand-200" to="/for-restaurants">For restaurants</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-surface/60">Company</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><Link className="hover:text-brand-200" to="/about">About</Link></li>
            <li><Link className="hover:text-brand-200" to="/download" data-testid="footer-download">Download the app</Link></li>
            <li><Link className="hover:text-brand-200" to="/stories">Community stories</Link></li>
            <li><Link className="hover:text-brand-200" to="/contact">Contact</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-surface/60">Legal</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><Link className="hover:text-brand-200" to="/terms" data-testid="footer-terms">Terms &amp; conditions</Link></li>
            <li><Link className="hover:text-brand-200" to="/privacy" data-testid="footer-privacy">Privacy policy</Link></li>
            <li><Link className="hover:text-brand-200" to="/refunds" data-testid="footer-refunds">Refund &amp; cancellation</Link></li>
            <li><Link className="hover:text-brand-200" to="/charges" data-testid="footer-charges">Charges &amp; fees</Link></li>
            <li><Link className="hover:text-brand-200" to="/partners" data-testid="footer-partners">Fares &amp; how to join</Link></li>
            <li><Link className="hover:text-brand-200" to="/grievance" data-testid="footer-grievance">Grievance &amp; report fraud</Link></li>
            <li><Link className="hover:text-brand-200" to="/delete-account" data-testid="footer-delete-account">Delete your account</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-surface/10">
        <div className="kt-container flex flex-col items-start justify-between gap-2 py-5 text-xs text-surface/50 sm:flex-row sm:items-center">
          <span>&copy; {new Date().getFullYear()} KohimaTaxi &amp; Delivery. Kohima, Nagaland, India.</span>
          <span>kohimataxi.com</span>
        </div>
      </div>
    </footer>
  );
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("kt_cookie_consent")) setVisible(true);
  }, []);
  const choose = (value) => {
    localStorage.setItem("kt_cookie_consent", value);
    setVisible(false);
  };
  if (!visible) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6" data-testid="cookie-banner">
      <div className="kt-card mx-auto flex max-w-3xl flex-col gap-4 p-5 sm:flex-row sm:items-center">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand"><Cookie className="h-5 w-5" /></span>
        <p className="flex-1 text-sm leading-relaxed text-ink-soft">
          We use essential cookies to keep you signed in, and optional analytics cookies to improve KohimaTaxi. Read our <Link to="/privacy" className="font-medium text-brand underline-offset-2 hover:underline">privacy policy</Link>.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => choose("essential")} data-testid="cookie-essential">Essential only</Button>
          <Button className="rounded-full bg-brand text-white hover:bg-brand-600" onClick={() => choose("all")} data-testid="cookie-accept-all">Accept all</Button>
        </div>
      </div>
    </div>
  );
}

export default function MarketingLayout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return (
    <div className="flex min-h-screen flex-col bg-surface text-ink">
      <Navbar />
      <InstallPromoBar />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
